import React, { useRef, useState, useEffect, useMemo } from 'react';
import { getReenactData } from '../services/api';
import { buildSegmentMap, lookupState, summarizeSessions } from '../utils/reenactMap';
import { formatDateTime, formatSeconds, formatShortDate } from '../utils/helpers';
import { useT } from '../utils/i18n';

// The narration audio is the master clock. Each animation frame we read its
// position, ask the segment map what the video should be doing, and nudge the
// video into place. These are the tolerances before we bother issuing a seek.
const SEEK_THRESHOLD = 0.3;   // seconds — paused-position mismatch
const DRIFT_THRESHOLD = 0.3;  // seconds — playing-position drift
// After a corrective seek, give the video this long to settle before checking
// drift again, so a slow decode can't trigger a seek storm.
const CORRECTION_COOLDOWN_MS = 300;

const NARRATOR_LABEL = { Won: 'ว่าน', Waan: 'หวาน' };

// Playback rates offered for a reenact. The narration and the video always share
// one rate — the segment map is expressed in *media* time, so equal rates keep
// `position = anchorPos + elapsed audio` exact at any speed.
const SPEED_STEPS = [1, 1.25, 1.5, 1.75, 2, 2.5, 3];
const SPEED_KEY = 'pp_reenact_speed';   // remembered across videos and reloads
const NARRATION_VOL_KEY = 'pp_reenact_narration_vol';
const VIDEO_VOL_KEY = 'pp_reenact_video_vol';
const VOL_MIN = 0;
const VOL_MAX = 400;
const VOL_DEFAULT = 100;

const readStoredSpeed = () => {
  try {
    const stored = Number(window.localStorage.getItem(SPEED_KEY));
    return SPEED_STEPS.includes(stored) ? stored : 1;
  } catch (e) {
    return 1;   // private mode / storage disabled
  }
};

const readStoredVolume = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null || raw === undefined || raw === '') return VOL_DEFAULT;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < VOL_MIN || value > VOL_MAX) return VOL_DEFAULT;
    return value;
  } catch (e) {
    return VOL_DEFAULT;   // private mode / storage disabled
  }
};

// m:ss, for the stretch of narration a session occupies.
const clock = (seconds) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// mm:ss (or h:mm:ss when >= 1 hour), for the live timecode display.
const formatTimecode = (seconds) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

/**
 * Reenact panel: replays a narrator's recorded narration while auto-driving the
 * <video> (via the player's imperative handle) from the logged play/pause/seek.
 * Visible to admin and narrator alike. S3-hosted videos only.
 */
const ReenactPanel = ({ videoId, playerRef, disabled = false, disabledReason = null, narrators = {}, onActiveChange }) => {
  const available = ['Won', 'Waan'].filter((n) => narrators[n]);
  const t = useT();

  const [narrator, setNarrator] = useState(available[0] || 'Won');
  const [mode, setMode] = useState('multi');       // 'multi' | 'single'
  const [sessionIndex, setSessionIndex] = useState(0);
  // Sessions for the current narrator, fetched up front so the single-session
  // dropdown is populated and selectable *before* Reenact is pressed.
  const [sessionList, setSessionList] = useState([]);
  // Processed is the default: it is what a reviewer listens to. Falls back to raw
  // below when the narrator has no processed file.
  const [source, setSource] = useState('processed'); // 'raw' | 'processed'
  const [audioAvail, setAudioAvail] = useState({ raw: true, processed: true });
  const [speed, setSpeed] = useState(readStoredSpeed);
  const [active, setActive] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [playHint, setPlayHint] = useState(false);
  // Playback volumes as 0..400 percent (gain 0..4), default 100%.
  const [narrationVolPct, setNarrationVolPct] = useState(() => readStoredVolume(NARRATION_VOL_KEY));
  const [videoVolPct, setVideoVolPct] = useState(() => readStoredVolume(VIDEO_VOL_KEY));

  const audioRef = useRef(null);
  const segmentsRef = useRef(null);
  const audioDurationRef = useRef(0);
  const correctingUntilRef = useRef(0);
  const resumeAtRef = useRef(null);        // audio time to restore after a src swap
  // Which session the narration playhead is inside, mirrored into state only when
  // it changes so the chip highlight doesn't re-render every animation frame.
  const activeSessionRef = useRef(null);
  const [activeSession, setActiveSession] = useState(null);
  // The rate we intend to play at, and whether the element is loaded enough for a
  // `ratechange` to be believable (see handleRateChange).
  const speedRef = useRef(speed);
  const rateReadyRef = useRef(false);
  // Web Audio boost for the narration, mirroring the video player's graph
  // (MediaElementSource -> Gain -> DynamicsCompressor limiter -> destination), so
  // the narration can also exceed the element's 100% cap up to 400%.
  const narrationCtxRef = useRef(null);
  const narrationSourceRef = useRef(null);
  const narrationGainRef = useRef(null);

  // Real-time timecode HUD DOM refs (direct manipulation avoids 60fps React re-renders)
  const narrationTimeDisplayRef = useRef(null);
  const videoTimeDisplayRef = useRef(null);
  const videoStateBadgeRef = useRef(null);

  // Keep the narrator selection valid if availability changes (e.g. a new upload).
  useEffect(() => {
    if (!available.includes(narrator) && available.length > 0) {
      setNarrator(available[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [narrators]);

  // Prefetch the narrator's sessions so the single-session dropdown can be filled
  // in before Start, and note which audio sources exist so the Source select can
  // fall back before it is used. The URLs themselves are re-fetched by handleStart.
  // Skipped while active (the live `data` drives the list then) and degrades to an
  // empty list on any error (dropdown falls back to a single placeholder option).
  useEffect(() => {
    if (active || available.length === 0 || !available.includes(narrator)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getReenactData(videoId, narrator);
        const sessions = res?.data?.sessions;
        if (cancelled) return;
        setSessionList(Array.isArray(sessions) ? sessions : []);
        setAudioAvail({
          raw: !!res?.data?.audio?.raw_url,
          processed: !!res?.data?.audio?.processed_url,
        });
      } catch (e) {
        if (!cancelled) setSessionList([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, narrator, narrators, active]);

  // Keep the source on something that actually exists: a narrator with raw-only
  // audio (never reprocessed) gets Raw instead of a dead Processed default.
  useEffect(() => {
    if (active) return;
    if (source === 'processed' && !audioAvail.processed && audioAvail.raw) setSource('raw');
    if (source === 'raw' && !audioAvail.raw && audioAvail.processed) setSource('processed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioAvail, active]);

  // Snap the session selection to a real session whenever the list changes.
  useEffect(() => {
    if (active) return;
    if (sessionList.length > 0 && !sessionList.some((s) => s.index === sessionIndex)) {
      setSessionIndex(sessionList[0].index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionList]);

  const audioUrl = data
    ? (source === 'raw' ? data.audio.raw_url : data.audio.processed_url)
    : null;

  // Where each session sits in the narration file, and which stretch of video it
  // narrates — the jumper's model. Always the stitched (multi) offsets: jumping is
  // navigation of the audio file and is independent of which session drives the
  // video, so in single mode you can jump out of the mapped session's slice (the
  // video then simply holds where that session's replay left it).
  const summaries = useMemo(() => summarizeSessions(data?.sessions || []), [data]);

  // Build the narration gain+limiter graph once per audio element (a
  // MediaElementSource can only be created once). Survives a source swap (same
  // element); torn down on stop when the element unmounts.
  const ensureNarrationGraph = () => {
    if (narrationSourceRef.current) return true;
    const audio = audioRef.current;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!audio || !AudioCtx) return false;
    try {
      const ctx = new AudioCtx();
      const source = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      gain.gain.value = 1.0;
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.knee.value = 6;
      limiter.ratio.value = 20;
      limiter.attack.value = 0.003;
      limiter.release.value = 0.25;
      source.connect(gain).connect(limiter).connect(ctx.destination);
      narrationCtxRef.current = ctx;
      narrationSourceRef.current = source;
      narrationGainRef.current = gain;
      return true;
    } catch (e) {
      console.error('Narration boost unavailable:', e);
      return false;
    }
  };

  const teardownNarrationGraph = () => {
    if (narrationCtxRef.current) narrationCtxRef.current.close().catch(() => {});
    narrationCtxRef.current = null;
    narrationSourceRef.current = null;
    narrationGainRef.current = null;
  };

  const applyNarrationVolume = (level) => {
    const audio = audioRef.current;
    if (!audio) return;
    const v = Math.max(0, level);
    if (v <= 1 && !narrationSourceRef.current) {
      audio.volume = v;
      return;
    }
    if (ensureNarrationGraph()) {
      if (narrationCtxRef.current.state === 'suspended') narrationCtxRef.current.resume().catch(() => {});
      narrationGainRef.current.gain.value = v;
      audio.volume = 1;
    } else {
      audio.volume = Math.min(1, v);
    }
  };

  const handleNarrationVol = (e) => {
    const pct = Number(e.target.value);
    setNarrationVolPct(pct);
    try { window.localStorage.setItem(NARRATION_VOL_KEY, String(pct)); } catch (err) { /* ignore */ }
    applyNarrationVolume(pct / 100);
  };

  const handleVideoVol = (e) => {
    const pct = Number(e.target.value);
    setVideoVolPct(pct);
    try { window.localStorage.setItem(VIDEO_VOL_KEY, String(pct)); } catch (err) { /* ignore */ }
    playerRef.current?.setVideoVolume(pct / 100);
  };

  const singleSession = () => {
    if (!data) return null;
    return data.sessions.find((s) => s.index === sessionIndex) || data.sessions[0];
  };

  const rebuildMap = () => {
    if (!data) return;
    if (mode === 'single') {
      // Whole-file semantics: play the narration from 0:00 as if the file had been
      // created in only the selected session. Rebase the session to audio_offset 0
      // and map it over the whole file (clampToAudio stretches the last span to the
      // real end); no slice bounds — the entire file *is* this session's replay.
      const s = singleSession();
      segmentsRef.current = buildSegmentMap(
        s ? [{ ...s, audio_offset: 0 }] : [], audioDurationRef.current,
      );
    } else {
      segmentsRef.current = buildSegmentMap(data.sessions, audioDurationRef.current);
    }
  };

  const updateDisplays = (T) => {
    const audio = audioRef.current;
    const segments = segmentsRef.current;
    if (!audio || !segments) return;
    const curT = typeof T === 'number' ? T : audio.currentTime;
    const desired = lookupState(segments, curT);
    if (narrationTimeDisplayRef.current) {
      const narrText = formatTimecode(curT);
      if (narrationTimeDisplayRef.current.textContent !== narrText) {
        narrationTimeDisplayRef.current.textContent = narrText;
      }
    }
    if (videoTimeDisplayRef.current) {
      const vidText = desired.position != null ? formatTimecode(desired.position) : '—';
      if (videoTimeDisplayRef.current.textContent !== vidText) {
        videoTimeDisplayRef.current.textContent = vidText;
      }
    }
    if (videoStateBadgeRef.current) {
      let stateLabel = t('Paused');
      let badgeClass = 'badge bg-secondary ms-2 align-middle';
      if (!audio.paused) {
        if (desired.playing) {
          stateLabel = t('Playing');
          badgeClass = 'badge bg-success ms-2 align-middle';
        } else {
          stateLabel = t('Holding');
          badgeClass = 'badge bg-warning text-dark ms-2 align-middle';
        }
      }
      if (videoStateBadgeRef.current.textContent !== stateLabel) {
        videoStateBadgeRef.current.textContent = stateLabel;
        videoStateBadgeRef.current.className = badgeClass;
      }
    }
  };

  // Snap the video to whatever the current narration position maps to, without
  // waiting for the animation loop (used at start and on every narration seek).
  const applyImmediate = () => {
    const audio = audioRef.current;
    const player = playerRef.current;
    if (!audio || !player || !segmentsRef.current) return;
    const desired = lookupState(segmentsRef.current, audio.currentTime);
    if (desired.position != null) {
      correctingUntilRef.current = performance.now() + CORRECTION_COOLDOWN_MS;
      player.seekTo(desired.position);
    }
    if (audio.paused || !desired.playing) player.pause();
    updateDisplays(audio.currentTime);
  };

  const correct = (player, position) => {
    correctingUntilRef.current = performance.now() + CORRECTION_COOLDOWN_MS;
    player.seekTo(position);
  };

  // --- speed: the <audio> element owns the rate, the video mirrors it ---
  // Setting only the audio element means Chrome's own playback-speed menu (on the
  // native audio controls) works too: its `ratechange` is pushed to the video by
  // handleRateChange, so the two can never end up on different rates.
  const handleSpeed = (e) => {
    const rate = Number(e.target.value);
    if (!Number.isFinite(rate) || rate <= 0) return;
    try { window.localStorage.setItem(SPEED_KEY, String(rate)); } catch (err) { /* ignore */ }
    speedRef.current = rate;
    if (audioRef.current) audioRef.current.playbackRate = rate;
    setSpeed(rate);
  };

  const handleRateChange = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const rate = audio.playbackRate;
    // The video always follows, whatever the reason for the change — the two must
    // never sit on different rates.
    playerRef.current?.setPlaybackRate(rate);
    if (!rateReadyRef.current) {
      // A fresh src resets the element to 1x and fires this before metadata lands.
      // That is a reset, not a request: leave the intent alone for
      // handleLoadedMetadata to re-assert, or switching Raw/Processed would quietly
      // drop the chosen speed.
      return;
    }
    speedRef.current = rate;
    setSpeed(rate);
  };

  // --- session jumper: move the master clock, the video follows ---
  // Assigning currentTime never changes the paused state, so a jump preserves
  // play/pause; applyImmediate snaps the video to the landing frame either way.
  const jumpToAudio = (t) => {
    const audio = audioRef.current;
    if (!audio) return;
    const dur = audioDurationRef.current;
    const max = Number.isFinite(dur) && dur > 0 ? Math.max(0, dur - 0.05) : Infinity;
    audio.currentTime = Math.min(Math.max(0, t), max);
    applyImmediate();
  };

  // Position of the playhead in `summaries` (-1 before the first session).
  const currentSummaryPos = () => {
    const T = audioRef.current ? audioRef.current.currentTime : 0;
    let pos = -1;
    summaries.forEach((s, i) => { if (T >= s.audioStart) pos = i; });
    return pos;
  };

  const handleRestart = () => jumpToAudio(0);

  const handlePrevSession = () => {
    const target = summaries[Math.max(0, currentSummaryPos() - 1)];
    jumpToAudio(target ? target.audioStart : 0);
  };

  const handleNextSession = () => {
    const target = summaries[Math.min(summaries.length - 1, currentSummaryPos() + 1)];
    if (target) jumpToAudio(target.audioStart);
  };

  // --- the sync loop, alive only while reenacting ---
  useEffect(() => {
    if (!active) return undefined;
    let raf;
    const tick = () => {
      const audio = audioRef.current;
      const player = playerRef.current;
      const segments = segmentsRef.current;
      if (audio && player && segments) {
        const T = audio.currentTime;
        const desired = lookupState(segments, T);
        const st = player.getState();
        if (st) {
          const cooldownOk = performance.now() > correctingUntilRef.current;
          const target = desired.position;
          // Both clocks advance `rate` media-seconds per wall-second, so a fixed
          // media-time tolerance gets `rate` times tighter as the speed rises —
          // scale it, or a slow decode at 3x provokes a seek storm.
          const rate = audio.playbackRate || 1;
          const seekTol = SEEK_THRESHOLD * rate;
          const driftTol = DRIFT_THRESHOLD * rate;
          if (audio.paused) {
            if (!st.paused) player.pause();
            if (target != null && cooldownOk && Math.abs(st.currentTime - target) > seekTol) {
              correct(player, target);
            }
          } else if (desired.playing && target != null) {
            if (st.paused) {
              correct(player, target);
              player.play();
            } else if (cooldownOk && Math.abs(st.currentTime - target) > driftTol) {
              correct(player, target);
            }
          } else {
            // Narration is playing but the video should hold (addition / paused span).
            if (!st.paused) player.pause();
            if (target != null && cooldownOk && Math.abs(st.currentTime - target) > seekTol) {
              correct(player, target);
            }
          }
        }

        // Update real-time timecode HUD
        updateDisplays(T);

        // Highlight the chip the playhead is inside, re-rendering only on change.
        let inside = null;
        for (const s of summaries) {
          if (T >= s.audioStart) inside = s.index;
        }
        if (inside !== activeSessionRef.current) {
          activeSessionRef.current = inside;
          setActiveSession(inside);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, playerRef, summaries]);

  // Release the player and the narration graph if the panel unmounts mid-reenact.
  useEffect(() => () => {
    teardownNarrationGraph();
    try { playerRef.current?.endReenact(); } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    setExpired(false);
    setPlayHint(false);
    try {
      const res = await getReenactData(videoId, narrator);
      const d = res.data;
      if (!d.sessions || d.sessions.length === 0) {
        setError(t('No reenactable session for this narrator.'));
        setLoading(false);
        return;
      }
      const url = source === 'raw' ? d.audio.raw_url : d.audio.processed_url;
      if (!url) {
        setError(t('No audio for this source — try switching Raw/Processed.'));
        setLoading(false);
        return;
      }
      if (mode === 'single' && !d.sessions.some((s) => s.index === sessionIndex)) {
        setSessionIndex(d.sessions[0].index);
      }
      audioDurationRef.current = 0;
      segmentsRef.current = null;
      resumeAtRef.current = null;
      activeSessionRef.current = null;
      rateReadyRef.current = false;
      setActiveSession(null);
      setData(d);
      setActive(true);
      if (onActiveChange) onActiveChange(true);
      playerRef.current?.beginReenact();
    } catch (err) {
      setError(`${t('Could not load reenact data')}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStop = () => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    teardownNarrationGraph();
    try { playerRef.current?.pause(); } catch (e) { /* ignore */ }
    try { playerRef.current?.endReenact(); } catch (e) { /* ignore */ }
    setActive(false);
    setData(null);
    rateReadyRef.current = false;
    activeSessionRef.current = null;
    setActiveSession(null);
    if (onActiveChange) onActiveChange(false);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audioDurationRef.current = audio.duration;
    rebuildMap();
    if (resumeAtRef.current != null) {
      audio.currentTime = resumeAtRef.current;
      resumeAtRef.current = null;
    }
    // Otherwise start at 0 — both modes play from the file's beginning now.
    applyImmediate();
    // A src assignment resets playbackRate to 1, so (re)assert the intended speed on
    // every load — start, Raw/Processed switch, expired-link refresh. The resulting
    // `ratechange` is what hands the rate to the video.
    rateReadyRef.current = true;
    audio.playbackRate = speedRef.current;
    playerRef.current?.setPlaybackRate(speedRef.current);
    applyNarrationVolume(narrationVolPct / 100);
    playerRef.current?.setVideoVolume(videoVolPct / 100);
    // The Start click is a user gesture; try to roll immediately. If the browser
    // blocks it, the audio's own play control is the fallback.
    audio.play().catch(() => setPlayHint(true));
  };

  const handleAudioPause = () => {
    try { playerRef.current?.pause(); } catch (e) { /* ignore */ }
  };

  const handleAudioPlay = () => {
    setPlayHint(false);
  };

  const handleAudioSeeked = () => {
    applyImmediate();
  };

  const handleAudioEnded = () => {
    try { playerRef.current?.pause(); } catch (e) { /* ignore */ }
  };

  const handleAudioError = () => {
    // Presigned URLs live ~2h; a long review can outlast one. Offer a refresh.
    if (active) setExpired(true);
  };

  const handleRefreshAudio = async () => {
    try {
      const res = await getReenactData(videoId, narrator);
      const at = audioRef.current ? audioRef.current.currentTime : null;
      if (at != null) resumeAtRef.current = at;
      rateReadyRef.current = false;   // a re-presigned src resets the element's rate
      setExpired(false);
      setData(res.data);
    } catch (err) {
      setError(`${t('Could not refresh audio link')}: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSourceChange = (e) => {
    if (active && audioRef.current) resumeAtRef.current = audioRef.current.currentTime;
    rateReadyRef.current = false;   // the new src will reset the element's rate
    setSource(e.target.value);
  };

  if (available.length === 0) {
    return null; // no recorded audio for either narrator yet
  }

  const sessionsForSelect = active && data ? data.sessions : sessionList;

  return (
    <div className="reenact-panel card p-3 mb-3">
      <h5>{t('Reenact')}</h5>

      <div className="row g-2 mb-2">
        <div className="col-6 col-md-3">
          <label className="form-label small mb-1">{t('Narrator')}</label>
          <select
            className="form-select form-select-sm"
            value={narrator}
            onChange={(e) => setNarrator(e.target.value)}
            disabled={active}
          >
            {available.map((n) => (
              <option key={n} value={n}>{NARRATOR_LABEL[n]}</option>
            ))}
          </select>
        </div>

        <div className="col-6 col-md-3">
          <label className="form-label small mb-1">{t('Mode')}</label>
          <select
            className="form-select form-select-sm"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={active}
          >
            <option value="multi">{t('All (stitched)')}</option>
            <option value="single">{t('Single session')}</option>
          </select>
        </div>

        {mode === 'single' && (
          <div className="col-6 col-md-3">
            <label className="form-label small mb-1">{t('Session')}</label>
            <select
              className="form-select form-select-sm"
              aria-label={t('Session')}
              value={sessionIndex}
              onChange={(e) => setSessionIndex(Number(e.target.value))}
              disabled={active}
            >
              {sessionsForSelect.length > 0
                ? sessionsForSelect.map((s) => (
                    <option key={s.index} value={s.index}>
                      {`#${s.index + 1} · ${formatDateTime(s.start)} · ${formatSeconds(s.duration)}`}
                    </option>
                  ))
                : <option value={sessionIndex}>{`#${sessionIndex + 1}`}</option>}
            </select>
          </div>
        )}

        <div className="col-6 col-md-3">
          <label className="form-label small mb-1">{t('Source')}</label>
          <select
            className="form-select form-select-sm"
            aria-label={t('Source')}
            value={source}
            onChange={handleSourceChange}
          >
            <option value="raw" disabled={!audioAvail.raw}>
              {audioAvail.raw ? t('Raw') : t('Raw (none)')}
            </option>
            <option value="processed" disabled={!audioAvail.processed}>
              {audioAvail.processed ? t('Processed') : t('Processed (none)')}
            </option>
          </select>
        </div>

        <div className="col-6 col-md-3">
          <label className="form-label small mb-1">{t('Speed')}</label>
          <select
            className="form-select form-select-sm"
            aria-label={t('Playback speed')}
            value={speed}
            onChange={handleSpeed}
          >
            {SPEED_STEPS.map((s) => (
              <option key={s} value={s}>{`${s}x`}</option>
            ))}
            {/* Chrome's native audio menu can set an off-list rate; show the truth. */}
            {!SPEED_STEPS.includes(speed) && (
              <option value={speed}>{`${speed}x`}</option>
            )}
          </select>
        </div>
      </div>

      {!active ? (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={handleStart}
          disabled={loading || disabled}
        >
          {loading ? t('Loading…') : `▶️ ${t('Reenact')}`}
        </button>
      ) : (
        <button type="button" className="btn btn-sm btn-danger" onClick={handleStop}>
          ⏹️ {t('Stop')}
        </button>
      )}

      {disabled && disabledReason && (
        <small className="text-muted d-block mt-2">{disabledReason}</small>
      )}

      {active && audioUrl && (
        <div className="mt-3">
          <div
            className="reenact-timecode-display card bg-light p-2 mb-2"
            aria-label={t('Reenact timecodes')}
            role="region"
          >
            <div className="d-flex justify-content-around align-items-center text-center">
              <div>
                <span className="text-muted small d-block">{t('Narration time')}</span>
                <span ref={narrationTimeDisplayRef} className="fw-bold font-monospace fs-5">00:00</span>
              </div>
              <div className="border-start ps-3">
                <span className="text-muted small d-block">{t('Video time')}</span>
                <div className="d-flex align-items-center justify-content-center">
                  <span ref={videoTimeDisplayRef} className="fw-bold font-monospace fs-5">00:00</span>
                  <span ref={videoStateBadgeRef} className="badge bg-secondary ms-2 align-middle">
                    {t('Paused')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {summaries.length > 0 && (
            <div className="reenact-jumper mb-2">
              <div className="form-label small mb-1" id="reenact-jump-label">{t('Jump to session')}</div>
              <div className="d-flex flex-wrap gap-2 mb-2" role="group" aria-labelledby="reenact-jump-label">
                {summaries.map((s, i) => (
                  <button
                    key={s.index}
                    type="button"
                    className={`btn btn-sm text-start ${s.index === activeSession ? 'btn-primary' : 'btn-outline-secondary'}`}
                    aria-pressed={s.index === activeSession}
                    onClick={() => jumpToAudio(s.audioStart)}
                    title={t('Jump the narration to session #{number}', { number: i + 1 })}
                  >
                    <span className="d-block">{`#${i + 1} · ${formatShortDate(s.start)}`}</span>
                    {/* Where this take sits in the narration — the thing the button
                        actually moves you to, so it needs no label to explain it. */}
                    <small className="d-block">{`${clock(s.audioStart)}–${clock(s.audioEnd)}`}</small>
                  </button>
                ))}
              </div>
              <div className="btn-group btn-group-sm" role="group" aria-label={t('Session transport')}>
                <button type="button" className="btn btn-outline-secondary" onClick={handleRestart}>
                  ⏮ {t('Restart')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={handlePrevSession}>
                  ◀ {t('Prev')}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={handleNextSession}>
                  {t('Next')} ▶
                </button>
              </div>
            </div>
          )}

          <audio
            ref={audioRef}
            src={audioUrl}
            controls
            crossOrigin="anonymous"
            className="w-100"
            aria-label={t('Narration playback')}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={handleAudioPlay}
            onPause={handleAudioPause}
            onSeeked={handleAudioSeeked}
            onRateChange={handleRateChange}
            onEnded={handleAudioEnded}
            onError={handleAudioError}
          >
            {t('Your browser does not support the audio tag.')}
          </audio>
          {playHint && (
            <small className="text-muted d-block mt-1">{t('Press play above to start')}</small>
          )}

          <div className="row g-3 mt-1">
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1 d-flex justify-content-between">
                <span>{t('Narration volume')}</span><span>{narrationVolPct}%</span>
              </label>
              <input
                type="range" className="form-range"
                min="0" max="400" step="5"
                value={narrationVolPct}
                onChange={handleNarrationVol}
                aria-label={t('Narration volume {percent} percent', { percent: narrationVolPct })}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small mb-1 d-flex justify-content-between">
                <span>{t('Video volume')}</span><span>{videoVolPct}%</span>
              </label>
              <input
                type="range" className="form-range"
                min="0" max="400" step="5"
                value={videoVolPct}
                onChange={handleVideoVol}
                aria-label={t('Video volume {percent} percent', { percent: videoVolPct })}
              />
            </div>
          </div>
        </div>
      )}

      {expired && (
        <div className="alert alert-warning mt-2 mb-0 py-2 small">
          {t('Audio link expired')}
          <button type="button" className="btn btn-sm btn-outline-warning ms-2" onClick={handleRefreshAudio}>
            {t('Refresh link')}
          </button>
        </div>
      )}

      {error && <div className="alert alert-danger mt-2 mb-0 py-2 small">{error}</div>}
    </div>
  );
};

export default ReenactPanel;
